import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, FileText, ArrowLeft } from "lucide-react";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "demo@test.com")
  .split(",")
  .map((e) => e.trim().toLowerCase());

const BLANK = {
  title: "", excerpt: "", body: "", category: "Essay Writing",
  cover_image: "", keywords: "", meta_description: "",
  author_name: "WriterSpark Team", status: "published", slug: "",
};

export default function AdminBlog() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null or {slug?, ...form}
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/blog/admin/posts");
      setPosts(data.posts || []);
      setCategories(data.categories || []);
    } catch { /* noop */ }
  };
  useEffect(() => { refresh(); }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_EMAILS.includes((user.email || "").toLowerCase())) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>Admin access required</h1>
        <p className="text-slate-600 text-sm mb-6">Only allowlisted admin accounts can edit blog posts.</p>
        <Link to="/app" className="text-indigo-600 font-bold">Back to dashboard</Link>
      </div>
    );
  }

  const newPost = () => setEditing({ ...BLANK });
  const editPost = async (slug) => {
    try {
      const { data } = await api.get(`/blog/admin/posts/${slug}`);
      setEditing({ ...BLANK, ...data.post });
    } catch { toast.error("Could not load post"); }
  };
  const deletePost = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    try { await api.delete(`/blog/admin/posts/${slug}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const savePost = async () => {
    if (!editing.title?.trim() || !editing.body?.trim()) {
      toast.error("Title and body are required"); return;
    }
    setBusy(true);
    try {
      const payload = { ...editing };
      if (editing.slug && posts.some((p) => p.slug === editing.slug)) {
        // Update existing — call PUT
        await api.put(`/blog/admin/posts/${editing.slug}`, payload);
        toast.success("Post saved");
      } else {
        await api.post("/blog/admin/posts", payload);
        toast.success("Post created");
      }
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="space-y-5 max-w-4xl" data-testid="admin-blog-editor">
        <button onClick={() => setEditing(null)} data-testid="back-to-list"
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-900">
          <ArrowLeft size={14}/> Back to posts
        </button>

        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
          {editing.slug ? "Edit post" : "New post"}
        </h1>

        <div className="tactile-card p-6 space-y-4">
          <F label="Title" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} testId="post-title"/>
          <F label="Slug (optional — auto-generated from title)" v={editing.slug} on={(v) => setEditing({ ...editing, slug: v })} testId="post-slug" placeholder="my-post-slug"/>
          <F label="Excerpt (1-2 sentences)" v={editing.excerpt} on={(v) => setEditing({ ...editing, excerpt: v })} testId="post-excerpt" rows={2} textarea/>
          <F label="Meta description (SEO)" v={editing.meta_description} on={(v) => setEditing({ ...editing, meta_description: v })} testId="post-meta-desc" rows={2} textarea/>
          <F label="Keywords (comma-separated)" v={editing.keywords} on={(v) => setEditing({ ...editing, keywords: v })} testId="post-keywords"/>
          <label className="block">
            <span className="text-xs uppercase font-bold tracking-widest text-slate-500">Category</span>
            <select data-testid="post-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              className="mt-1 w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-3 py-2 outline-none text-sm">
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <F label="Cover image URL" v={editing.cover_image} on={(v) => setEditing({ ...editing, cover_image: v })} testId="post-cover"/>
          <F label="Author name" v={editing.author_name} on={(v) => setEditing({ ...editing, author_name: v })} testId="post-author"/>
          <F label="Body (Markdown — # ## ### **bold** *italic* - lists [text](url))" v={editing.body} on={(v) => setEditing({ ...editing, body: v })} testId="post-body" rows={14} textarea/>
          <label className="block">
            <span className="text-xs uppercase font-bold tracking-widest text-slate-500">Status</span>
            <select data-testid="post-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
              className="mt-1 w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-3 py-2 outline-none text-sm">
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </label>

          <button onClick={savePost} disabled={busy} data-testid="post-save"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-5 py-2.5 disabled:opacity-60">
            <Save size={14}/> {busy ? "Saving…" : "Save post"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-blog-list">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Blog posts</h1>
        <button onClick={newPost} data-testid="new-post-btn"
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-5 py-2.5">
          <Plus size={14}/> New post
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="tactile-card p-10 text-center" data-testid="admin-blog-empty">
          <FileText size={28} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-sm text-slate-500">No posts yet. Click "New post" to create your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {posts.map((p) => (
            <div key={p.slug} data-testid={`admin-post-${p.slug}`} className="tactile-card p-4 flex items-center justify-between gap-3">
              <button onClick={() => editPost(p.slug)} className="text-left flex-1 min-w-0">
                <div className="font-bold truncate">{p.title}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {p.category} · {p.status} · {p.published_at?.slice(0,10) || "—"} · /{p.slug}
                </div>
              </button>
              <button onClick={() => deletePost(p.slug)} className="text-slate-400 hover:text-rose-600 p-2"
                data-testid={`admin-post-del-${p.slug}`}>
                <Trash2 size={16}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const F = ({ label, v, on, placeholder, textarea, rows = 1, testId }) => (
  <label className="block">
    <span className="text-xs uppercase font-bold tracking-widest text-slate-500">{label}</span>
    {textarea ? (
      <textarea
        data-testid={testId}
        rows={rows}
        value={v || ""}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-3 py-2 outline-none text-sm font-mono"
      />
    ) : (
      <input
        data-testid={testId}
        value={v || ""}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-400 px-3 py-2 outline-none text-sm"
      />
    )}
  </label>
);
